import React, { useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  LinearProgress,
  Chip,
  Alert,
  Paper,
  Grid
} from '@mui/material';
import {
  CloudUpload,
  Camera,
  VideoCall,
  Delete,
  Preview,
  CheckCircle,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';

/**
 * FileUploadZone Component
 * 
 * Comprehensive file upload component with:
 * - Drag and drop functionality
 * - Camera capture for mobile
 * - File validation and preview
 * - Progress tracking
 * - Multiple file support
 */
const FileUploadZone = ({
  onFilesSelected,
  onFileRemoved,
  maxFiles = 10,
  maxFileSize = 10 * 1024 * 1024, // 10MB
  acceptedTypes = {
    'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.webp'],
    'video/*': ['.mp4', '.avi', '.mov', '.wmv'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx']
  },
  uploadProgress = {},
  uploadedFiles = [],
  errors = [],
  allowCamera = true,
  allowVideo = true,
  title = "Upload Evidence Documents",
  description = "Drag and drop files here, or click to select files"
}) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [dropErrors, setDropErrors] = useState([]);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoInputRef = useRef(null);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    setIsDragActive(false);
    
    // Handle rejected files
    if (rejectedFiles.length > 0) {
      const availableSlots = Math.max(maxFiles - uploadedFiles.length, 0);
      const rejectedErrors = rejectedFiles.map(file => {
        const tooManyFiles = file.errors.some(err => err.code === 'too-many-files');
        const fileTooLarge = file.errors.some(err => err.code === 'file-too-large');
        const invalidType = file.errors.some(err => err.code === 'file-invalid-type');

        if (tooManyFiles) {
          return availableSlots === 0
            ? `You already selected ${maxFiles} files. Remove one before adding another.`
            : `You can add ${availableSlots} more file${availableSlots === 1 ? '' : 's'} for a maximum of ${maxFiles}.`;
        }

        if (fileTooLarge) {
          return `${file.file.name} is too large. Maximum file size is ${formatFileSize(maxFileSize)}.`;
        }

        if (invalidType) {
          return `${file.file.name} is not a supported file type.`;
        }

        return `${file.file.name}: ${file.errors.map(err => err.message).join(', ')}`;
      });
      setDropErrors([...new Set(rejectedErrors)]);
      console.error('Rejected files:', rejectedErrors);
    } else {
      setDropErrors([]);
    }

    // Handle accepted files
    if (acceptedFiles.length > 0) {
      setDropErrors([]);
      onFilesSelected(acceptedFiles);
    }
  }, [maxFiles, maxFileSize, onFilesSelected, uploadedFiles.length]);

  const { getRootProps, getInputProps, isDragActive: dropzoneActive } = useDropzone({
    onDrop,
    accept: acceptedTypes,
    maxSize: maxFileSize,
    maxFiles: maxFiles - uploadedFiles.length,
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false)
  });

  const handleCameraCapture = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      onFilesSelected(files);
    }
  };

  const handleVideoCapture = (event) => {
    const files = Array.from(event.target.files);
    if (files.length > 0) {
      onFilesSelected(files);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return '🖼️';
    if (file.type.startsWith('video/')) return '🎥';
    if (file.type === 'application/pdf') return '📄';
    if (file.type.includes('word')) return '📝';
    return '📎';
  };

  const getUploadStatus = (fileName) => {
    if (uploadProgress[fileName] === 100) return 'completed';
    if (uploadProgress[fileName] > 0) return 'uploading';
    return 'pending';
  };

  const formatUploadError = (error) => {
    if (!error) return null;
    if (typeof error === 'string') return error;
    if (error.message) return error.file ? `${error.file}: ${error.message}` : error.message;
    if (error.errors) return error.file ? `${error.file}: ${error.errors}` : error.errors;
    return String(error);
  };

  const displayErrors = [...errors, ...dropErrors]
    .map(formatUploadError)
    .filter(Boolean);

  return (
    <Box>
      {/* Upload Zone */}
      <Paper
        {...getRootProps()}
        elevation={isDragActive || dropzoneActive ? 8 : 2}
        sx={{
          p: 4,
          textAlign: 'center',
          cursor: 'pointer',
          border: `2px dashed ${isDragActive || dropzoneActive ? '#1976d2' : '#ccc'}`,
          backgroundColor: isDragActive || dropzoneActive ? '#f3f8ff' : '#fafafa',
          transition: 'background-color 0.2s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            backgroundColor: '#f0f8ff',
            borderColor: '#1976d2'
          }
        }}
      >
        <input {...getInputProps()} />
        
        <CloudUpload 
          sx={{ 
            fontSize: 64, 
            color: isDragActive || dropzoneActive ? '#1976d2' : '#ccc',
            mb: 2 
          }} 
        />
        
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {isDragActive ? 'Drop the files here...' : description}
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
          >
            Select Files
          </Button>

          {allowCamera && (
            <Button
              variant="outlined"
              startIcon={<Camera />}
              onClick={(e) => {
                e.stopPropagation();
                cameraInputRef.current?.click();
              }}
            >
              Take Photo
            </Button>
          )}

          {allowVideo && (
            <Button
              variant="outlined"
              startIcon={<VideoCall />}
              onClick={(e) => {
                e.stopPropagation();
                videoInputRef.current?.click();
              }}
            >
              Record Video
            </Button>
          )}
        </Box>

        {/* File type information */}
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
          Max file size: {formatFileSize(maxFileSize)} • Max files: {maxFiles}
          <br />
          Supported: Images, Videos, PDF, Word documents
        </Typography>
      </Paper>

      {/* Hidden file inputs for camera/video */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={Object.keys(acceptedTypes).join(',')}
        style={{ display: 'none' }}
        onChange={(e) => {
          const files = Array.from(e.target.files);
          if (files.length > 0) {
            onFilesSelected(files);
          }
        }}
      />

      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleCameraCapture}
      />

      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={handleVideoCapture}
      />

      {/* Error Display */}
      {displayErrors.length > 0 && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Upload Errors:
          </Typography>
          {displayErrors.map((error, index) => (
            <Typography key={index} variant="body2">
              • {error}
            </Typography>
          ))}
        </Alert>
      )}

      {/* File List */}
      {uploadedFiles.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Files ({uploadedFiles.length}/{maxFiles})
          </Typography>
          
          <Grid container spacing={2}>
            {uploadedFiles.map((file, index) => {
              const status = getUploadStatus(file.name);
              const progress = uploadProgress[file.name] || 0;
              
              return (
                <Grid item xs={12} key={index}>
                  <Paper elevation={1} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography sx={{ fontSize: '1.5rem' }}>
                        {getFileIcon(file)}
                      </Typography>
                      
                      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                        <Typography variant="body1" noWrap>
                          {file.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatFileSize(file.size)}
                        </Typography>
                        
                        {status === 'uploading' && (
                          <Box sx={{ mt: 1 }}>
                            <LinearProgress 
                              variant="determinate" 
                              value={progress} 
                              sx={{ mb: 0.5 }}
                            />
                            <Typography variant="caption">
                              {progress}% uploaded
                            </Typography>
                          </Box>
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {status === 'completed' && (
                          <Chip
                            icon={<CheckCircle />}
                            label="Uploaded"
                            color="success"
                            size="small"
                          />
                        )}
                        
                        {status === 'uploading' && (
                          <Chip
                            label="Uploading..."
                            color="primary"
                            size="small"
                          />
                        )}

                        {file.type.startsWith('image/') && (
                          <IconButton
                            size="small"
                            onClick={() => {
                              // Preview functionality
                              const url = URL.createObjectURL(file);
                              window.open(url, '_blank');
                            }}
                          >
                            <Preview />
                          </IconButton>
                        )}

                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onFileRemoved(file, index)}
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default FileUploadZone;
